import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser } from "../redux/Slice/authSlice";
import { logout as logoutRequest } from "../services/auth";
import { ROLE_LABELS } from "../utils/format";
import WhatsAppSupport from "../components/WhatsAppSupport";
import NavIcon from "../components/NavIcon";
import { toast as notify } from "../utils/toast";
import AnalyticsTab from "./admin/AnalyticsTab";
import BillingTab from "./admin/BillingTab";
import BulkPurchaseTab from "./admin/BulkPurchaseTab";
import CustomersTab from "./admin/CustomersTab";
import FranchiseesTab from "./admin/FranchiseesTab";
import MarketingTab from "./admin/MarketingTab";
import MigrationTab from "./admin/MigrationTab";
import BlogTab from "./admin/BlogTab";
import SeoTab from "./admin/SeoTab";
import StoreSeoTab from "./admin/StoreSeoTab";
import OverviewTab from "./admin/OverviewTab";
import OrdersTab from "./admin/OrdersTab";
import { getPendingOrders } from "../services/admin";
import PaymentsTab from "./admin/PaymentsTab";
import ProfileTab from "./admin/ProfileTab";
import SettingsTab from "./admin/SettingsTab";
import StoreSettingsTab from "./admin/StoreSettingsTab";
import SetupChecklist from "./admin/SetupChecklist";
import StoreGuide from "./admin/StoreGuide";
import SitesTab from "./admin/SitesTab";
import StaffTab from "./admin/StaffTab";
import SuggestionsTab, { SUGGESTIONS_SEEN_EVENT } from "./admin/SuggestionsTab";
import { onOrdersChanged } from "../utils/ordersChanged";
import { can } from "./admin/permissions";
import { getUnreadSuggestions } from "../services/suggestions";
import "./admin.css";
import { stopTracking, track } from "../utils/activityTracker";
import OwnezzyLogo from "../components/OwnezzyLogo";
import InstallApp from "../components/InstallApp";
import { returnToMyAccount, viewingAsBy } from "../utils/viewAs";
import { AlertBell } from "../components/Alerts";
import { LimitWarnings } from "../components/UsageLimits";
import Tutorial, { TutorialButton } from "../components/Tutorial";
import StarterContentModal from "../components/StarterContentModal";

// What each role can open. The role comes from the signed-in account, so there is nothing to switch.
const NAV = {
  superadmin: ["Overview", "All websites", "Demo stores", "Store settings", "Customers", "Franchisees", "Payments", "Analytics", "Data migration", "Blog", "Marketing", "SEO Hub", "Suggestions", "Settings", "Profile"],
  subadmin: ["Overview", "My websites", "Customers", "Bulk purchase", "Marketing", "Suggestions", "Profile"],
  storeadmin: ["Overview", "Order management", "My websites", "Store settings", "SEO", "Staff", "Analytics", "Data migration", "Profile", "Marketing", "Billing", "Suggestions"],
};
// Shorter names for the phone tab bar
const SHORT_NAMES = { "Order management": "Orders", "All websites": "Websites", "My websites": "Websites" };
// Staff see their store owner's websites, plus Store settings when the owner allowed it
const navFor = (user) => (user.role === "staff" ? ["My websites", ...(can(user, "settings") ? ["Store settings"] : []), "Profile"] : NAV[user.role]);
// Phones get a bottom tab bar like an app. It fits 5 buttons; with more tabs, the last button is "More".
const TAB_BAR_SLOTS = 5;
// Each tab has its own address, /admin/<tab> (e.g. /admin/data-migration), so a link, a refresh or Back opens it
export const tabPath = (name) => `/admin/${name.toLowerCase().replace(/\s+/g, "-")}`;

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const navItems = useMemo(() => navFor(user), [user]);
  // The tab comes from the address; /admin (or a tab this account doesn't have) opens the first one
  const { tab: tabSlug } = useParams();
  const tab = navItems.find((name) => tabPath(name) === `/admin/${tabSlug}`) || navItems[0];
  useEffect(() => {
    if (tabPath(tab) !== location.pathname) navigate(`${tabPath(tab)}${window.location.hash}`, { replace: true });
  }, [tab, location.pathname, navigate]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // phones: the sidebar opens as a side drawer from the ☰ button
  // Which floating card (setup steps or the how-to guide) is open on a tab other than Overview; another tab folds it
  const [floatOpen, setFloatOpen] = useState(null);
  useEffect(() => { setFloatOpen(null); }, [tab]);
  useEffect(() => { track("tab", tab); }, [tab]); // Admin Tracker: which tabs are opened
  const toggleFloat = useCallback((name) => (show) => setFloatOpen(show ? name : null), []);
  // Unread Suggestions messages, shown as a badge on the tab (checked every 30 seconds)
  const [unread, setUnread] = useState(0);
  const hasSuggestions = navItems.includes("Suggestions");
  useEffect(() => {
    if (!hasSuggestions) return undefined;
    let live = true;
    const check = () => getUnreadSuggestions().then(({ data }) => live && setUnread(data.unread)).catch(() => {});
    check();
    const timer = setInterval(check, 30000);
    window.addEventListener(SUGGESTIONS_SEEN_EVENT, check); // a chat was opened or answered
    return () => { live = false; clearInterval(timer); window.removeEventListener(SUGGESTIONS_SEEN_EVENT, check); };
  }, [hasSuggestions, tab]);
  // Orders still to send out across the owner's websites, a red badge on Order management and My websites (checked every minute)
  const [pendingOrders, setPendingOrders] = useState(0);
  const hasMyWebsites = navItems.includes("My websites");
  useEffect(() => {
    if (!hasMyWebsites) return undefined;
    let live = true;
    const check = () => getPendingOrders().then(({ data }) => live && setPendingOrders(data.pending)).catch(() => {});
    check();
    const timer = setInterval(check, 60000);
    const stopListening = onOrdersChanged(check); // an order placed or answered
    return () => { live = false; clearInterval(timer); stopListening(); };
  }, [hasMyWebsites, tab]);
  const count = (n, label) => <em className="nav-badge" aria-label={`${n} ${label}`}>{n > 99 ? "99+" : n}</em>;
  const badge = (name) => {
    if (name === "Suggestions" && unread > 0) return count(unread, "unread");
    if (["Order management", "My websites"].includes(name) && pendingOrders > 0) return count(pendingOrders, "pending orders");
    return null;
  };
  const initial = (user.name || "A").charAt(0).toUpperCase();

  // Opening a tab changes the address and starts at the top of the page, as in an app
  const setTab = useCallback((name) => {
    if (tabPath(name) !== window.location.pathname) navigate(tabPath(name));
    setMoreOpen(false);
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [navigate]);
  const tabBarItems = navItems.length > TAB_BAR_SLOTS ? navItems.slice(0, TAB_BAR_SLOTS - 1) : navItems;
  const moreItems = navItems.slice(tabBarItems.length);

  // Links like /admin#store-payment (from the setup checklist, or shared by support) open the Store settings tab, and
  // StoreSettingsForm then scrolls to that card
  useEffect(() => {
    const openFromHash = () => {
      if (!window.location.hash.startsWith("#store-")) return;
      const target = ["Store settings", "Profile"].find((name) => navItems.includes(name));
      // keeps the #store-… part, which StoreSettingsForm scrolls to
      if (target && tabPath(target) !== window.location.pathname) navigate(`${tabPath(target)}${window.location.hash}`, { replace: true });
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [navItems, navigate]);

  // While the drawer is open the page behind it doesn't scroll, and Escape closes it
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [navOpen]);

  const signOut = async () => {
    if (viewingAsBy()) return returnToMyAccount(); // viewing as someone: "sign out" goes back to the Super Admin
    stopTracking(); // the last report goes out while the token still works
    await logoutRequest().catch(() => {}); // revokes the token on the server
    dispatch(logout());
    navigate("/login");
  };

  const props = { user, notify, setTab };
  const screens = {
    Overview: <OverviewTab {...props} />,
    "Order management": <OrdersTab {...props} />,
    "All websites": <SitesTab {...props} />,
    "My websites": <SitesTab {...props} />,
    "Demo stores": <SitesTab key="demo" {...props} demo />, // keyed: its own search and filters
    Customers: <CustomersTab {...props} />,
    Franchisees: <FranchiseesTab {...props} />,
    Payments: <PaymentsTab {...props} />,
    Analytics: <AnalyticsTab {...props} />,
    "Data migration": <MigrationTab {...props} />,
    Blog: <BlogTab {...props} />,
    Marketing: <MarketingTab {...props} />,
    "SEO Hub": <SeoTab {...props} />,
    "Bulk purchase": <BulkPurchaseTab {...props} />,
    Billing: <BillingTab {...props} />,
    Settings: <SettingsTab {...props} />,
    "Store settings": <StoreSettingsTab {...props} />,
    SEO: <StoreSeoTab {...props} />,
    Staff: <StaffTab {...props} />,
    Suggestions: <SuggestionsTab {...props} />,
    Profile: <ProfileTab {...props} />,
  };

  return (
    <div className="admin-shell">
      <div className={`admin-drawer-backdrop ${navOpen ? "is-open" : ""}`} onClick={() => setNavOpen(false)} aria-hidden="true" />
      <aside className={`admin-sidebar ${navOpen ? "is-open" : ""}`} id="admin-nav-drawer" aria-label="Main navigation">
        <button className="admin-drawer-close" onClick={() => setNavOpen(false)} aria-label="Close menu">✕</button>
        <button className="admin-logo" onClick={() => setTab("Overview")} aria-label="Ownezzy: go to overview"><OwnezzyLogo wide /></button>
        <div className="admin-workspace"><span className="workspace-dot" /> Multi-store workspace</div>
        <div className="admin-nav-label">WORKSPACE</div>
        {navItems.map((name) => (
          <button key={name} data-tour={name} className={`admin-nav ${tab === name ? "selected" : ""}`} onClick={() => setTab(name)}>
            <NavIcon name={name} />{name}{badge(name)}
          </button>
        ))}
        <InstallApp />
        <div className="sidebar-bottom">
          <div className="avatar">{initial}</div>
          <div><b>{user.name}</b><small>{ROLE_LABELS[user.role]}</small></div>
        </div>
        <button className="admin-nav drawer-signout" onClick={signOut}><NavIcon name="Sign out" />Sign out</button>
      </aside>
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="topbar-title">
            <button className="topbar-menu" onClick={() => setNavOpen(true)} aria-label="Open menu" aria-expanded={navOpen} aria-controls="admin-nav-drawer">
              <span /><span /><span />
            </button>
            <span className="topbar-mark" aria-hidden="true"><OwnezzyLogo /></span>
            <span className="breadcrumb">Workspace / </span><b>{tab}</b>
          </div>
          <div className="topbar-right">
            {/* Store owners: play the first sign-in tour again */}
            {user.role === "storeadmin" && !viewingAsBy() && <TutorialButton />}
            <AlertBell />
            <span className="secure-label">● {user.email || user.phone}</span>
            <button className="outline-btn topbar-signout" onClick={signOut}><NavIcon name="Sign out" /><span>Sign out</span></button>
            <button className="top-avatar" onClick={() => setTab("Profile")} aria-label="Open profile">{initial}</button>
          </div>
        </header>
        <div className="admin-content">
          {/* Store owners: what is left to finish comes first, the "how to" guide below it */}
          {/* Store owners: storage or emails 70% used or more (closable until the next day) */}
          {user.role === "storeadmin" && <LimitWarnings />}
          {/* In the page on Overview; on any other tab each folds into a floating button (one opens at a time) */}
          {user.role === "storeadmin" && <SetupChecklist user={user} floating={tab !== "Overview"} open={floatOpen === "setup"} onToggle={toggleFloat("setup")} />}
          {user.role === "storeadmin" && <StoreGuide user={user} setTab={setTab} floating={tab !== "Overview"} open={floatOpen === "guide"} onToggle={toggleFloat("guide")} />}
          {screens[tab]}
        </div>
      </main>

      <nav className="mobile-tabbar" aria-label="Main">
        {tabBarItems.map((name) => (
          <button key={name} data-tour={name} className={tab === name ? "selected" : ""} onClick={() => setTab(name)} aria-current={tab === name ? "page" : undefined}>
            <NavIcon name={name} /><span>{SHORT_NAMES[name] || name}</span>{badge(name)}
          </button>
        ))}
        {moreItems.length > 0 && (
          <button data-tour="More" className={moreItems.includes(tab) ? "selected" : ""} onClick={() => setMoreOpen(true)} aria-haspopup="dialog">
            <NavIcon name="More" /><span>More</span>{moreItems.map(badge).find(Boolean)}
          </button>
        )}
      </nav>
      {moreOpen && (
        <div className="mobile-sheet-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setMoreOpen(false)}>
          <div className="mobile-sheet" role="dialog" aria-modal="true" aria-label="More">
            <span className="mobile-sheet-handle" aria-hidden="true" />
            {moreItems.map((name) => (
              <button key={name} className={tab === name ? "selected" : ""} onClick={() => setTab(name)}><NavIcon name={name} />{name}{badge(name)}</button>
            ))}
            <InstallApp className="sheet-install" />
            <button onClick={signOut}><NavIcon name="Sign out" />Sign out</button>
          </div>
        </div>
      )}
      <WhatsAppSupport user={user} />
      {/* Store owners: the first sign-in tour (once; a Super Admin can turn it off) */}
      {user.role === "storeadmin" && !viewingAsBy() && <Tutorial setTab={setTab} />}
      {/* Store owners: sample products choice modal after tutorial */}
      {user.role === "storeadmin" && !viewingAsBy() && <StarterContentModal user={user} notify={notify} />}
    </div>
  );
}
